/**
 * Hook to manage a Fastly-cached GQL query.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/1928003702/useFastlyQuery.
 */
import * as wbd from "@khanacademy/wonder-blocks-data";
import type {GAEModule, GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import {DefaultFastlyQueryScope} from "./constants.ts";
import {toNormalizedOperation} from "./to-normalized-operation.ts";
import {Persist} from "./types.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * Options for Fastly queries.
 */
interface FastlyQueryOptions<
    TData extends wbd.ValidCacheData,
    TVariables extends Record<any, any>,
> {
    /**
     * Callback invoked when the operation has resolved and the cached result
     * is updated. It is passed the latest cached value (which is also the
     * value that the hook would return on the next invocation).
     *
     * If omitted, the hook will manipulate state internally to trigger
     * a component update.
     *
     * NOTE: We currently do not invoke this if another hook instance modified
     * the same cache value. This is because we do not expect that to be a use
     * we would encounter. Let frontend-infra know if you believe you need this.
     */
    cacheUpdated?: (current: wbd.Result<TData>) => void;
    /**
     * When true, regardless of the cache state, the query will not be
     * executed. The current cache state will be returned, which will be
     * {status: "loading"} if the query is not in the cache.
     */
    skip?: boolean | null | undefined;
    /**
     * The variables your query needs to execute.
     */
    variables?: TVariables;
    /**
     * The caching policy to attach to this query's result.
     * Defaults to Persist.UntilPublish.
     */
    cachingPolicy?: (typeof Persist)[keyof typeof Persist];
    /**
     * Override the default module.
     * Defaults to the value provided by GqlRouter.
     */
    module?: GAEModule;
    /**
     * Override the default locale.
     * Useful for targetting a different localized topic tree, for example.
     * Defaults to the value provided by GqlRouter.
     */
    targetLocale?: string;
    /**
     * Override the default curriculum.
     * Defaults to the value provided by GqlRouter.
     */
    curriculum?: string;
}

/**
 * Hook to request GraphQL queries cached in Fastly.
 */
export const useFastlyQuery = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    {
        skip,
        variables,
        module: backendModule,
        targetLocale,
        cachingPolicy = Persist.UntilPublish,
        curriculum,
        cacheUpdated,
    }: FastlyQueryOptions<TData, TVariables> = Object.freeze({}),
): wbd.Result<TData> => {
    // We're going to use the WB Data gqlFetch directly because otherwise we
    // would need to duplicate the logic of the webapp version in order to
    // get the query information that we need for our cache key.

    // First, extract the Wonder Blocks Data operation and verify it.
    const wbdOperation = toNormalizedOperation(operation);
    if (wbdOperation.type !== "query") {
        throw new KAError(
            "Mutations are not valid for useFastlyQuery",
            Errors.InvalidInput,
        );
    }

    // Normally, we rely on the gqlFetch to handle combining our partial
    // context with the defaults specified with GqlRouter. However, in this
    // case we need that info here to be able to get a request ID.
    const requestIdBuilder = useRequestIdBuilder();

    // Get the identifier for this request.
    const requestId = requestIdBuilder(operation, variables, {
        module: backendModule,
        targetLocale,
        curriculum,
        fastlyCaching: cachingPolicy,
    });

    // Get the gqlFetch function.
    // This is configured to use the default's from GqlRouter, etc.
    const gqlFetch = wbd.useGql();

    // Now we let Wonder Blocks Data do the hard work.
    const currentValue = wbd.useHydratableEffect(
        requestId,
        () =>
            gqlFetch(wbdOperation, {
                variables,
                context: {
                    module: backendModule,
                    targetLocale,
                    curriculum,
                    fastlyCaching: cachingPolicy,
                },
            }),
        {
            skip: skip === true,
            onResultChanged: cacheUpdated,
            scope: DefaultFastlyQueryScope,
        },
    );
    return currentValue;
};
